import { useEffect, useMemo, useState } from "react";
import { Box, Callout, Spinner } from "@radix-ui/themes";
import { Icon } from '@cloudscape-design/components';
import S2sChatBot from "../s2s";
import { getScenarioById } from "../helper/api";
import type { ScenarioBundle } from "../helper/api";

function getScenarioIdFromUrl(): number | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("id");
  if (!raw) return null;

  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function Scenario() {
  const scenarioId = useMemo(() => getScenarioIdFromUrl(), []);

  const [bundle, setBundle] = useState<ScenarioBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        if (!scenarioId) {
          throw new Error("Missing or invalid scenario id in URL. Expected /scenario?id=<number>.");
        }

        const data = await getScenarioById(scenarioId);
        
        if (!cancelled) setBundle(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load scenario");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scenarioId]);

  return (
    <Box>
      {/* Status messages */}
        {loading && (
          <Callout.Root m="6" variant="surface">
            <Callout.Icon>
              <Spinner size="3" />
            </Callout.Icon>
            <Callout.Text weight="bold" size="3">
              Loading scenario...
            </Callout.Text>
          </Callout.Root>
        )}

        {!loading && error && (
          <Callout.Root>
            <Callout.Icon>
              <Icon name="status-negative" />
            </Callout.Icon>
            <Callout.Text>
              {error}
            </Callout.Text>
          </Callout.Root>
        )}

      {/* Two column layout */}
      {!loading && !error && bundle && (
        <S2sChatBot
          title={bundle.scenario.scenario_name}
          scenario={bundle.scenario}
          student={bundle.student}
          systemPrompt={bundle.initial_prompt}
          promptParams={{
            scenario_id: bundle.scenario.scenario_id,
            student_id: bundle.student.student_id,
            student_name: bundle.student.student_name,
            gender: bundle.student.gender,
          }}
        />
      )}
    </Box>
  );
}
